/* /share-import — landing page for the PWA Web Share Target.
 *
 * Android share-sheet flow: User taps Share on Instagram/TikTok → picks Recipe Filer →
 * lands here with ?url=… (or ?text=… containing the URL).
 *
 * If logged in: POST /api/share-import → recipe goes straight into the Inbox cookbook,
 * we show a toast with a "Move to →" action, then redirect to /dashboard.
 *
 * If logged out: bounce to /login?next=/share-import?<same-query> so the share survives auth.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { recipePath, cookbookPath } from "@/lib/slug";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LinkSimple, CheckCircle, X as XIcon } from "@phosphor-icons/react";

export default function ShareImport() {
  const [params] = useSearchParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const ranRef = useRef(false);   // strict-mode guard — fire-and-forget exactly once
  const [status, setStatus] = useState("idle"); // idle | working | done | error
  const [imported, setImported] = useState(null); // { recipe_id, cookbook_id, title, source_name }
  const [errorMsg, setErrorMsg] = useState("");
  const [moveOpen, setMoveOpen] = useState(false);
  const [cookbooks, setCookbooks] = useState([]);
  // Inline paste-to-import — a fallback the user can use when iOS's Shortcut
  // Input arrived empty (which Instagram frequently causes). No shortcut rebuild
  // required — just paste from clipboard and hit Import.
  const [pasteUrl, setPasteUrl] = useState("");
  const [pasting, setPasting] = useState(false);

  // Extract URL from any of: `url` param (preferred), `text` param, `title` param,
  // or a last-ditch scan of the entire location.search string. Instagram's Share
  // Sheet often ships a caption blob / attachment metadata through the shortcut
  // instead of a bare URL, so scanning every source makes the flow robust against
  // real-world share payloads (Instagram, TikTok, Facebook Reels, WhatsApp).
  const rawUrl = (params.get("url") || "").trim();
  const text = (params.get("text") || "").trim();
  const title = (params.get("title") || "").trim();
  const findUrl = (s) =>
    (s.match(/https?:\/\/\S+/i)?.[0] || "").replace(/[).,"'`\]]+$/, "");
  // The receiver reads the URL from every possible source iOS could have
  // stuffed it into: standard ?url=, PWA share ?text=, PWA share ?title=,
  // the raw undecoded location.search (some Shortcut variants forget to
  // percent-encode), AND the URL fragment (window.location.hash) — the
  // Text-action + URL-Encode Shortcut pattern occasionally sends URLs as
  // a #hash instead of a query string on older iOS.
  const rawSearch = typeof window !== "undefined" ? (window.location.search || "") : "";
  const rawHash = typeof window !== "undefined" ? (window.location.hash || "").replace(/^#/, "") : "";
  const extractedUrl = /^https?:\/\//i.test(rawUrl)
    ? rawUrl.replace(/[).,"'`\]]+$/, "")
    : findUrl(rawUrl)
      || findUrl(text)
      || findUrl(title)
      || findUrl(decodeURIComponent(rawSearch))
      || findUrl(decodeURIComponent(rawHash))
      || findUrl(rawHash);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      // Bounce to login with this exact URL as `next` so the share survives the auth round-trip
      const next = `/share-import?${params.toString()}`;
      nav(`/login?next=${encodeURIComponent(next)}`, { replace: true });
      return;
    }
    if (ranRef.current) return;
    ranRef.current = true;

    if (!extractedUrl) {
      setStatus("error");
      setErrorMsg("We couldn't find a link in what you shared. Open Recipe Filer directly and paste the URL.");
      return;
    }

    setStatus("working");
    (async () => {
      try {
        const { data } = await api.post("/share-import", { url: extractedUrl, text, title });
        setImported(data);
        setStatus("done");
        // Preload cookbooks for the move dropdown (only the user's *real* books — exclude the Inbox itself)
        const { data: cbs } = await api.get("/cookbooks");
        // Include co-edited cookbooks so users can file into books friends
        // have shared with them (with edit rights). View-only invites are
        // excluded — the backend would 403 anyway.
        setCookbooks([...(cbs.owned || []), ...(cbs.co_edited || [])].filter((c) => c.cookbook_id !== data.cookbook_id));
        // Success feedback — matches the messaging on ScanRecipe. Short
        // haptic pulse for phones, since iOS Share Sheet users are always
        // on mobile. Toast is louder than a status-only screen so if the
        // user has left the tab it still catches their attention.
        try { navigator.vibrate?.([15, 40, 15]); } catch { /* ignore */ }
        toast.success("Import successful", {
          description: data.source_name
            ? `Saved to your Inbox from ${data.source_name}. Tap "Move to →" below to file it into a cookbook.`
            : "Saved to your Inbox. Tap 'Move to →' below to file it into a cookbook.",
          duration: 5000,
        });
      } catch (e) {
        setStatus("error");
        try { navigator.vibrate?.([100, 60, 100]); } catch { /* ignore */ }
        // Mobile-friendly failure message with an actionable next step.
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || "";
        let msg;
        if (!e?.response) {
          msg = "Import didn't reach us. Check your connection and try again.";
        } else if (status === 400 || detail.toLowerCase().includes("could not")) {
          msg = "That page doesn't look like a recipe we can read. Try opening it in RecipeFiler and pasting the direct URL.";
        } else if (status >= 500) {
          msg = "Something went wrong on our end. Please try again in a minute.";
        } else {
          msg = detail || "Couldn't import that link. Try opening it directly in RecipeFiler.";
        }
        setErrorMsg(msg);
      }
    })();
  }, [loading, user, extractedUrl, params, nav, text, title]);

  const moveTo = async (cookbookId) => {
    if (!imported) return;
    try {
      await api.post(`/recipes/${imported.recipe_id}/move`, { target_cookbook_id: cookbookId });
      toast.success("Moved");
      nav(recipePath(imported));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    }
  };

  // Manual paste-and-import — fires when the iOS Shortcut Input arrived empty
  // (typical for Instagram Reels). The user pastes the URL from clipboard
  // and we re-run the same /api/share-import call.
  const importPasted = async (overrideUrl) => {
    const source = (overrideUrl ?? pasteUrl).trim();
    const candidate = findUrl(source) || source;
    if (!/^https?:\/\//i.test(candidate)) {
      toast.error("Paste a full URL starting with http:// or https://");
      return;
    }
    setPasting(true);
    try {
      const { data } = await api.post("/share-import", { url: candidate });
      setImported(data);
      setStatus("done");
      const { data: cbs } = await api.get("/cookbooks");
      setCookbooks([...(cbs.owned || []), ...(cbs.co_edited || [])].filter((c) => c.cookbook_id !== data.cookbook_id));
      toast.success(
        data.source_name ? `Saved to Inbox · from ${data.source_name}` : "Saved to Inbox",
        { duration: 4500 },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't import that URL");
    } finally {
      setPasting(false);
    }
  };

  // Auto-import on paste — the moment the user pastes a URL into the input, we
  // fire the same import call. Removes the "then tap Import" step, which is
  // the last bit of friction on the Instagram rescue path.
  const handlePaste = (e) => {
    const pasted = (e.clipboardData?.getData("text") || "").trim();
    const candidate = findUrl(pasted) || pasted;
    if (/^https?:\/\//i.test(candidate)) {
      // Let React's state update; fire on the next tick with the fresh value.
      setPasteUrl(candidate);
      setTimeout(() => { if (!pasting) importPasted(candidate); }, 50);
    }
  };

  // When we land on the error state, try to preload the user's clipboard into
  // the paste box. iOS requires an explicit user gesture for clipboard reads
  // (fails silently otherwise) — that's fine, the box remains editable.
  // If we successfully find a URL on the clipboard, auto-fire the import so
  // the user lands directly on the success card (zero-tap rescue path).
  useEffect(() => {
    if (status !== "error" || pasteUrl) return;
    (async () => {
      try {
        if (navigator?.clipboard?.readText) {
          const clip = await navigator.clipboard.readText();
          const url = (clip || "").trim();
          if (url && /^https?:\/\//i.test(url)) {
            setPasteUrl(url);
            // Small delay so the input reflects the value first
            setTimeout(() => importPasted(url), 100);
          }
        }
      } catch { /* clipboard denied — user will paste manually */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once when status flips to error; importPasted/pasteUrl are closed over intentionally
  }, [status]);

  if (loading || (!user && extractedUrl)) {
    return <CenteredCard><Spinner /><p className="text-rf-ink-soft mt-4">Signing you in to receive the share…</p></CenteredCard>;
  }

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 py-16">
      <p className="overline mb-3" data-testid="share-import-overline">Share to Recipe Filer</p>

      {status === "working" && (
        <CenteredCard>
          <Spinner />
          <p className="font-display text-3xl font-black text-rf-ink mt-6 mb-2">Saving to your Inbox…</p>
          <p className="text-rf-ink-soft break-all">{extractedUrl}</p>
        </CenteredCard>
      )}

      {status === "done" && imported && (
        <div className="card-recipe text-center" data-testid="share-import-done" role="status" aria-live="polite">
          <CheckCircle size={56} weight="duotone" className="mx-auto text-rf-orange mb-4" />
          <p className="overline mb-2">Saved to your Inbox</p>
          <h1 className="font-display text-3xl md:text-4xl font-black text-rf-ink mb-2 leading-tight">
            {imported.title}
          </h1>
          {imported.source_name && (
            <p className="text-rf-ink-soft text-sm mb-6 inline-flex items-center gap-1.5 justify-center">
              <LinkSimple size={14} weight="bold" /> from {imported.source_name}
            </p>
          )}
          <div className="flex flex-wrap gap-2 justify-center mt-6">
            <Link to={recipePath(imported)} data-testid="share-open-recipe" className="btn-amber">Open recipe</Link>
            <Link to={cookbookPath(imported.cookbook_id)} data-testid="share-open-inbox" className="btn-outline-ink">Open Inbox</Link>
            {cookbooks.length > 0 && (
              <div className="relative">
                <button data-testid="share-move-btn" onClick={() => setMoveOpen((o) => !o)} className="btn-outline-ink">Move to →</button>
                {moveOpen && (
                  <div className="absolute right-0 mt-2 bg-white rounded-xl shadow-xl border border-rf-ink/10 min-w-[240px] p-2 z-10 text-left">
                    {cookbooks.map((c) => (
                      <button
                        key={c.cookbook_id}
                        data-testid={`share-move-to-${c.cookbook_id}`}
                        onClick={() => moveTo(c.cookbook_id)}
                        className="block w-full text-left px-3 py-2 hover:bg-rf-cream-alt rounded-lg text-sm font-semibold"
                      >
                        {c.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <p className="text-xs text-rf-ink-soft italic mt-8">
            All shared recipes land in your <b>Inbox</b> first — file them into a permanent cookbook whenever you&apos;re ready.
          </p>
          {typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && (
            <p className="text-xs text-rf-ink-soft mt-2" data-testid="share-done-ios-tip">
              <Link to="/ios-share-setup" className="text-rf-orange font-bold underline">Make sharing one-tap on iOS →</Link>
            </p>
          )}
        </div>
      )}

      {status === "error" && (
        <div className="card-recipe text-center" data-testid="share-import-error">
          <XIcon size={48} weight="bold" className="mx-auto text-red-500 mb-4" />
          <h1 className="font-display text-2xl font-black text-rf-ink mb-3">Couldn&apos;t import that share</h1>
          <p className="text-rf-ink-soft mb-6">{errorMsg}</p>

          {/* Inline paste-and-import — the ONE-tap rescue when iOS delivered
              empty Shortcut Input (typical for Instagram Reels, or an
              incorrectly-built shortcut). If clipboard already holds a URL
              we've pre-filled it. Pasting a URL auto-fires the import — no
              button-tap needed on the rescue path. */}
          <div className="bg-rf-amber/15 border border-rf-amber/50 rounded-2xl p-4 mb-6 text-left" data-testid="share-import-paste-panel">
            <p className="font-display font-extrabold text-rf-ink text-sm mb-1">
              Quick rescue — paste any URL below
            </p>
            <p className="text-xs text-rf-ink-soft mb-3 leading-relaxed">
              Paste the recipe link here and it&apos;ll import automatically. If this keeps happening every share, your Shortcut is built with the wrong action type &mdash; <Link to="/ios-share-setup" className="font-bold text-rf-orange underline">rebuild it here</Link> (takes 60 seconds).
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="url"
                inputMode="url"
                value={pasteUrl}
                onChange={(e) => setPasteUrl(e.target.value)}
                onPaste={handlePaste}
                placeholder="Paste an Instagram / TikTok / recipe URL…"
                data-testid="share-import-paste-input"
                className="input-rf flex-1"
                autoFocus
              />
              <button
                onClick={() => importPasted()}
                disabled={pasting || !pasteUrl.trim()}
                data-testid="share-import-paste-submit"
                className="btn-amber disabled:opacity-50 justify-center"
              >
                {pasting ? "Importing…" : "Import"}
              </button>
            </div>
          </div>

          {/* Debug block — shows exactly what iOS passed, so we can diagnose
              why the extraction failed. Always rendered on the error card. */}
          <details className="text-left bg-rf-cream-alt rounded-xl p-4 mb-6 text-xs" data-testid="share-import-debug">
            <summary className="cursor-pointer font-bold text-rf-ink text-sm">
              Show what iOS actually sent (for debugging)
            </summary>
            <div className="mt-3 space-y-2 font-mono break-all">
              <div><b className="text-rf-orange">?url</b> = {JSON.stringify(rawUrl)}</div>
              <div><b className="text-rf-orange">?text</b> = {JSON.stringify(text)}</div>
              <div><b className="text-rf-orange">?title</b> = {JSON.stringify(title)}</div>
              <div><b className="text-rf-orange">location.search</b> = {JSON.stringify(rawSearch)}</div>
              <div><b className="text-rf-orange">location.hash</b> = {JSON.stringify(typeof window !== "undefined" ? window.location.hash : "")}</div>
              <div><b className="text-rf-orange">extractedUrl</b> = {JSON.stringify(extractedUrl)}</div>
            </div>
            <p className="text-[11px] italic mt-3 text-rf-ink-soft">
              Screenshot this and send it to contactus@recipefiler.com if the share keeps failing — it tells us the exact payload iOS is passing.
            </p>
          </details>

          <div className="flex flex-wrap gap-2 justify-center">
            <Link to="/scan?mode=url" className="btn-outline-ink">Open full URL importer</Link>
            <Link to="/dashboard" className="btn-outline-ink">Back to dashboard</Link>
          </div>
          {typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && (
            <p className="text-xs text-rf-ink-soft mt-6">
              <Link to="/ios-share-setup" data-testid="share-error-ios-cta" className="text-rf-orange font-bold underline">Set up the iOS Share Sheet shortcut →</Link>
              {" "}so this works one-tap next time.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function CenteredCard({ children }) {
  return (
    <div className="card-recipe text-center py-12">{children}</div>
  );
}

function Spinner() {
  return (
    <div className="inline-block w-10 h-10 border-4 border-rf-amber border-t-rf-orange rounded-full animate-spin" aria-hidden="true" />
  );
}
